// IMPORTANT: We do NOT import slot_picker_modal/index.scss here. Importing
// SCSS via JS compiles it in isolation, without the theme SCSS variables
// (`$colors-slot-bg-color`, etc.) that `lib/tasks/sparkle/vite_themes.rake`
// prepends to the per-publishing-domain `_application_vite.scss` entrypoint.
// Without those vars the slot mixin's `!default` values win, producing the
// wrong (outlined-teal) button style. Instead the modal stylesheet must be
// imported from `_application_vite.scss` itself so the theme variables apply.

import.meta.glob("./views/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./templates/**/*.{js,hbs,jst.ejs}", { eager: true });
import "./helpers/resource_fetcher.js";
import "virtual:client-templates/app/assets/javascripts/appointment_booking/slot_picker_modal/templates";

window.AppointmentBooking ??= {};

// The filters the header builds its visit type scope from. A change to any other filter
// keeps the offered visit types.
const VISIT_TYPE_SCOPE_FILTERS = ["patient_type", "resource_type", "resource_id", "physician_id", "physician_id_type"];

// Preserve any sub-namespace populated by the eager `import.meta.glob` above
// (Vite hoists glob imports above explicit imports, so the views/templates
// child files run first and attach onto SlotPickerModal.{Views,Models,...}
// before this file replaces the namespace with the new class).
const __previousSlotPickerModal = AppointmentBooking.SlotPickerModal;

AppointmentBooking.SlotPickerModal = class SlotPickerModal {
  static Views = {};
  static Models = {};
  static Collections = {};
  static Helpers = {};
  static Events = {
    slotPick: "appointmentBookingSlotPickerModalHeader-slotPick",
    selectView: "appointmentBookingSlotPickerModal-selectView",
  };

  constructor({ focusDate, filters, options }) {
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSlotPick = this.handleSlotPick.bind(this);
    this.handleSelectedViewChanged = this.handleSelectedViewChanged.bind(this);
    this.handleModalHide = this.handleModalHide.bind(this);
    this.hide = this.hide.bind(this);
    this.showListView = this.showListView.bind(this);
    this.showCalendarView = this.showCalendarView.bind(this);
    this.makeListViewSelected = this.makeListViewSelected.bind(this);
    this.makeCalendarViewSelected = this.makeCalendarViewSelected.bind(this);
    this.showModal = this.showModal.bind(this);
    const defaults = {
      resource: true,
      header: {},
      list: {},
      calendar: {},
      class: "appointment-booking-slot-picker-modal",
    };

    this.options = _.defaults({}, options, defaults);

    if (!focusDate) {
      focusDate = new Date();
    }
    focusDate = moment.parseZone(focusDate);

    const resource = new Backbone.Model({ name: "", addresses: [], specialties: [] });
    filters = new Backbone.Model(filters);
    this.model = new Backbone.Model({ selectedView: null, filters, resource, focusDate });

    this.destroyed = false;

    this.modalElement = document.createElement("sp-modal");
    this.modalElement.classList.add("appointment-booking-slot-picker-modal");
    if (this.options.class) {
      this.modalElement.classList.add(this.options.class);
    }
    this.modalElement.setAttribute("width", "44rem");
    this.modalElement.setAttribute("max-width", "90vw");
    this.modalElement.setAttribute("no-mobile-content-padding", "");
    this.modalElement.setAttribute("expand-height-max", "");
    this.modalElement.dataset.analyticsElement = "slot-picker";
    this.modalElement.dataset.analyticsInteraction = "close";
    if (this.options.analyticsScope) {
      this.modalElement.dataset.analyticsScope = this.options.analyticsScope;
    }

    this.bodyContainer = document.createElement("div");
    this.bodyContainer.className = "appointment-modal-body-container";

    const views = [];

    if (this.options.resource) {
      const fetcher = new AppointmentBooking.SlotPickerModal.Helpers.ResourceFetcher();
      fetcher.fetch(filters.attributes).then((resourceAttributes) => resource.set(resourceAttributes));
    }

    if (this.options.list) {
      const listOptions = _.extend({ renderLinks: this.options.renderLinks }, this.options.list, {
        model: new Backbone.Model({ filters, resource, focusDate }),
      });
      this.listView = new this.constructor.Views.List(listOptions);
      views.push({ title: AppointmentBooking.config.i18n.slot_picker_modal.views.list, name: "list" });
    }

    if (this.options.calendar) {
      const calendarOptions = _.extend({ renderLinks: this.options.renderLinks }, this.options.calendar, {
        model: new Backbone.Model({ filters, resource, focusDate }),
      });
      this.calendarView = new this.constructor.Views.Calendar(calendarOptions);
      views.push({ title: AppointmentBooking.config.i18n.slot_picker_modal.views.calendar, name: "calendar" });
    }

    if (this.options.header) {
      const headerOptions = _.extend(
        {
          availabilitySelector: this.options.availabilitySelector,
          patientTypeSelector: this.options.patientTypeSelector,
          addressSelector: this.options.addressSelector,
        },
        this.options.header,
        {
          views,
          model: this.model,
        },
      );
      this.modalHeader = new this.constructor.Views.Header(headerOptions);
    }

    Backbone.Events.listenTo(filters, "change", this.handleFilterChange);
    Backbone.Events.listenTo(this.model, "change:selectedView", this.handleSelectedViewChanged);

    this.modalElement.addEventListener("close", this.handleModalHide);

    if (this.options.header && this.modalHeader) {
      const headerEl = this.modalHeader.render().el;

      const headerSlot = document.createElement("div");
      headerSlot.setAttribute("slot", "header");
      headerSlot.appendChild(headerEl);

      this.modalElement.appendChild(headerSlot);
    }

    if (this.listView != null ? this.listView.el : undefined) {
      this.bodyContainer.appendChild(this.listView.el);
    }
    if (this.calendarView != null ? this.calendarView.el : undefined) {
      this.bodyContainer.appendChild(this.calendarView.el);
    }
    this.modalElement.appendChild(this.bodyContainer);

    // Delegate events on @modalElement (not @bodyContainer) to catch header events
    // Header is in slot="header" which is outside @bodyContainer
    jQuery(this.modalElement).on(this.constructor.Events.slotPick, this.handleSlotPick);
    jQuery(this.modalElement).on(this.constructor.Events.selectView, (event, view) =>
      this.model.set("selectedView", view),
    );
  }

  // EVENT HANDLERS

  handleFilterChange(filters) {
    if (typeof this.options.onFilterChange === "function") {
      this.options.onFilterChange(filters);
    }
    if (this.options.header && _.some(VISIT_TYPE_SCOPE_FILTERS, (key) => filters.hasChanged(key))) {
      return this.modalHeader.scopeVisitTypes();
    }
  }

  handleSlotPick(event, slot) {
    return typeof this.options.onSlotPick === "function"
      ? this.options.onSlotPick(slot, event.target, this)
      : undefined;
  }

  handleSelectedViewChanged() {
    if (this.model.get("selectedView") === "calendar") {
      return this.makeCalendarViewSelected();
    } else {
      return this.makeListViewSelected() || this.makeCalendarViewSelected();
    }
  }

  handleModalHide() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;

    if (this.modalElement != null) {
      this.modalElement.removeEventListener("close", this.handleModalHide);
    }
    if (this.modalElement) {
      jQuery(this.modalElement).off();
    }

    if (this.model) {
      Backbone.Events.stopListening(this.model);
    }
    if (this.model != null ? this.model.get("filters") : undefined) {
      Backbone.Events.stopListening(this.model.get("filters"));
    }

    if (this.modalHeader != null) {
      this.modalHeader.remove();
    }
    if (this.listView != null) {
      this.listView.remove();
    }
    if (this.calendarView != null) {
      this.calendarView.remove();
    }

    if (this.modalElement != null) {
      this.modalElement.remove();
    }
    this.modalElement = null;
    return (this.bodyContainer = null);
  }

  // API

  hide() {
    if (!this.modalElement) {
      return;
    }
    return this.modalElement.removeAttribute("open");
  }

  showListView() {
    return this.model.set("selectedView", "list");
  }

  showCalendarView() {
    return this.model.set("selectedView", "calendar");
  }

  // PRIVATE

  makeListViewSelected() {
    if (!this.options.list) {
      return false;
    }

    this.listView.render();
    this.listView.$el.show();
    if (this.calendarView != null) {
      this.calendarView.$el.hide();
    }
    this.showModal();

    return true;
  }

  makeCalendarViewSelected() {
    if (!this.options.calendar) {
      return;
    }

    this.calendarView.render();
    if (this.listView != null) {
      this.listView.$el.hide();
    }
    this.calendarView.$el.show();
    return this.showModal();
  }

  showModal() {
    if (!this.modalElement) {
      return;
    }

    if (!document.body.contains(this.modalElement)) {
      document.body.appendChild(this.modalElement);
    }

    return this.modalElement.setAttribute("open", "");
  }
};

if (__previousSlotPickerModal) {
  // Deep-merge the sub-namespaces (Views, Models, Collections, Helpers,
  // Templates) so that any classes attached by eager glob children survive
  // the new class definition above.
  for (const key of ["Views", "Models", "Collections", "Helpers", "Templates"]) {
    if (__previousSlotPickerModal[key]) {
      Object.assign((AppointmentBooking.SlotPickerModal[key] ??= {}), __previousSlotPickerModal[key]);
    }
  }
}
